import apiClient from "../authentication/utils/core/axios";

/**
 * Trending API service object following the COMMENT structure
 */
const TrendingAPI = {
  /**
   * Create a trending item
   * @param {Object} itemData
   * @returns {Promise<Object>}
   */
  create: async (itemData) => {
    const response = await apiClient.post('/trending/', itemData);
    if (response.status !== 201) throw new Error('Failed to create trending item');
    return response.data;
  },

  /**
   * Fetch multiple trending items with pagination
   * @param {number} skip
   * @param {number} limit
   * @returns {Promise<Array>}
   */
  fetchAll: async (skip = 0, limit = 10) => {
    const response = await apiClient.get(`/trending/?skip=${skip}&limit=${limit}`);
    if (response.status !== 200) throw new Error('Failed to fetch trending items');
    return response.data;
  },

  /**
   * Fetch trending items for a particular stock
   */
  fetchTrendingPostsForStock: async (symbol, skip = 0, limit = 10) => {
    if (!symbol) return [];
    const response = await apiClient.get(`/trending/stock/${symbol}?skip=${skip}&limit=${limit}`);
    if (response.status !== 200) throw new Error('Failed to fetch trending posts for stock');
    return response.data; // Expecting a list of trending post objects
  },

  /**
   * Fetch a single trending item by ID
   * @param {string|number} itemId
   * @returns {Promise<Object>}
   */
  fetchOne: async (itemId) => {
    const response = await apiClient.get(`/trending/${itemId}`);
    if (response.status !== 200) throw new Error('Failed to fetch trending item');
    return response.data;
  },
  
  /**
   * Fetch trending posts for multiple stocks in batch
   * @param {Array<string>} symbols
   * @param {number} skip
   * @param {number} limit
   * @returns {Promise<Array>}
   */
  fetchTrendingBatch: async (symbols, skip = 0, limit = 10) => {
    const response = await apiClient.post('/trending/batch', {
      symbols,
      skip,
      limit
    });
    if (response.status !== 200) throw new Error('Failed to fetch batch trending');
    return response.data;
  },

  /**
   * Update an existing trending item
   * @param {string|number} itemId
   * @param {Object} itemData
   * @returns {Promise<Object>}
   */
  update: async (itemId, itemData) => {
    const response = await apiClient.put(`/trending/${itemId}`, itemData);
    if (response.status !== 200) throw new Error('Failed to update trending item');
    return response.data;
  },

  /**
   * Delete a trending item
   * @param {string|number} itemId
   * @returns {Promise<string|number>}
   */
  delete: async (itemId) => {
    const response = await apiClient.delete(`/trending/${itemId}`);
    if (response.status !== 200 && response.status !== 204) throw new Error('Failed to delete trending item');
    return itemId;
  },

  incrementView: async (postId) => {
    const response = await apiClient.post(`/posts/${postId}/view`);
    if (response.status !== 200) throw new Error('Failed to increment view count');
    return response.data;
  },
};

export default TrendingAPI